// src\services\firebase.js
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  updateEmail,
  updateProfile,
  confirmPasswordReset,
  applyActionCode,
  verifyPasswordResetCode,
  checkActionCode,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDocs,
  getDoc,
  deleteDoc,
  updateDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  startAt,
  endAt,
  endBefore,
  limitToLast,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  Timestamp,
  writeBatch,
  increment,
  addDoc,
  onSnapshot,
  collectionGroup,
  documentId,
  getCountFromServer,
  runTransaction,
  enableNetwork,
  disableNetwork,
  connectFirestoreEmulator,
  enableIndexedDbPersistence,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  CACHE_SIZE_UNLIMITED,
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
  updateMetadata,
  uploadString,
} from 'firebase/storage';
import {
  getAnalytics,
  logEvent,
  setCurrentScreen,
  setUserId,
  setUserProperties,
  isSupported,
} from 'firebase/analytics';
import { getPerformance, trace } from 'firebase/performance';
import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
  getString,
  getNumber,
  getBoolean,
  getAll,
  setLogLevel,
} from 'firebase/remote-config';

// =============================================
// FIREBASE CONFIGURATION
// =============================================

const firebaseConfig = {
  apiKey: import.meta.env.VITE_APP_API_KEY,
  authDomain: import.meta.env.VITE_APP_AUTHDOMAIN,
  projectId: import.meta.env.VITE_APP_PROJECT_ID,
  storageBucket: import.meta.env.VITE_APP_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_APP_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_APP_ID,
  measurementId: import.meta.env.VITE_APP_MEAUSERMENT_ID,
};

// Validate config
const isValidConfig = Object.values(firebaseConfig).every(
  value => value && value !== '',
);

if (!isValidConfig) {
  console.error(
    '❌ Firebase configuration is missing. Please check your .env file',
  );
}

// =============================================
// INITIALIZE FIREBASE
// =============================================

let app = null;
let auth = null;
let db = null;
let storage = null;
let analytics = null;
let performance = null;
let remoteConfig = null;
let provider = null;

try {
  if (isValidConfig) {
    app = initializeApp(firebaseConfig);

    // Initialize Firestore
    db = getFirestore(app);

    auth = getAuth(app);
    storage = getStorage(app);
    provider = new GoogleAuthProvider();

    provider.setCustomParameters({
      prompt: 'select_account',
    });

    // Initialize Analytics (only in production)
    if (typeof window !== 'undefined' && import.meta.env.PROD) {
      isSupported()
        .then(supported => {
          if (supported) {
            analytics = getAnalytics(app);
            console.log('✅ Firebase Analytics initialized');
          }
        })
        .catch(() => {});
    }

    // Initialize Performance (only in production)
    if (typeof window !== 'undefined' && import.meta.env.PROD) {
      try {
        performance = getPerformance(app);
        console.log('✅ Firebase Performance initialized');
      } catch (e) {
        console.warn('Performance not supported:', e);
      }
    }

    // Initialize Remote Config
    if (typeof window !== 'undefined') {
      try {
        remoteConfig = getRemoteConfig(app);
        remoteConfig.settings = {
          minimumFetchIntervalMillis: 3600000, // 1 hour
          fetchTimeoutMillis: 10000,
        };
        remoteConfig.defaultConfig = {
          maintenance_mode: 'false',
          api_version: '1.0.0',
          max_upload_size: '10485760',
        };
        console.log('✅ Firebase Remote Config initialized');
      } catch (e) {
        console.warn('Remote Config not supported:', e);
      }
    }

    console.log('✅ Firebase initialized successfully');
  }
} catch (error) {
  console.error('❌ Firebase initialization error:', error);
}

// =============================================
// COLLECTION REFERENCES
// =============================================

// Users
export const USERS_COLLECTION = 'users';
export const usersRef = collection(db, USERS_COLLECTION);

// Projects
export const PROJECTS_COLLECTION = 'projects';
export const projectsRef = collection(db, PROJECTS_COLLECTION);

// Categories
export const CATEGORIES_COLLECTION = 'categories';
export const categoriesRef = collection(db, CATEGORIES_COLLECTION);

// Contact Messages
export const CONTACT_MESSAGES_COLLECTION = 'contactMessages';
export const contactMessagesRef = collection(db, CONTACT_MESSAGES_COLLECTION);

// Hire Inquiries
export const HIRE_INQUIRIES_COLLECTION = 'hireInquiries';
export const hireInquiriesRef = collection(db, HIRE_INQUIRIES_COLLECTION);

export const READY_MADE_INQUIRIES_COLLECTION = 'readyMadeInquiries';
export const readyMadeInquiriesRef = collection(
  db,
  READY_MADE_INQUIRIES_COLLECTION,
);

// Subscription Inquiries
export const SUBSCRIPTION_INQUIRIES_COLLECTION = 'subscriptionInquiries';
export const subscriptionInquiriesRef = collection(
  db,
  SUBSCRIPTION_INQUIRIES_COLLECTION,
);

// Admin Logs
export const ADMIN_LOGS_COLLECTION = 'admin_logs';
export const adminLogsRef = collection(db, ADMIN_LOGS_COLLECTION);

// Analytics
export const ANALYTICS_COLLECTION = 'analytics';
export const analyticsRef = collection(db, ANALYTICS_COLLECTION);

// Site Settings
export const SITE_SETTINGS_COLLECTION = 'site_settings';
export const siteSettingsRef = collection(db, SITE_SETTINGS_COLLECTION);

// User Data
export const USER_DATA_COLLECTION = 'user_data';
export const userDataRef = collection(db, USER_DATA_COLLECTION);

// Page Views
export const PAGE_VIEWS_COLLECTION = 'page_views';
export const pageViewsRef = collection(db, PAGE_VIEWS_COLLECTION);

// Events
export const EVENTS_COLLECTION = 'events';
export const eventsRef = collection(db, EVENTS_COLLECTION);

// Notifications
export const NOTIFICATIONS_COLLECTION = 'notifications';
export const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);

// Subscribers
export const SUBSCRIBERS_COLLECTION = 'subscribers';
export const subscribersRef = collection(db, SUBSCRIBERS_COLLECTION);

// Reviews
export const REVIEWS_COLLECTION = 'reviews';
export const reviewsRef = collection(db, REVIEWS_COLLECTION);

// Blogs
export const BLOGS_COLLECTION = 'blogs';
export const blogsRef = collection(db, BLOGS_COLLECTION);

// =============================================
// HELPER FUNCTIONS FOR AUTH
// =============================================

// Get current user token
export const getCurrentUserToken = async () => {
  if (!auth?.currentUser) return null;
  try {
    return await auth.currentUser.getIdToken();
  } catch (error) {
    console.error('Error getting token:', error);
    return null;
  }
};

// Get current user claims
export const getCurrentUserClaims = async () => {
  if (!auth?.currentUser) return null;
  try {
    const result = await auth.currentUser.getIdTokenResult();
    return result.claims;
  } catch (error) {
    console.error('Error getting claims:', error);
    return null;
  }
};

// Refresh token
export const refreshToken = async () => {
  if (!auth?.currentUser) return null;
  try {
    return await auth.currentUser.getIdToken(true);
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
};

// Update user profile
export const updateUserProfile = async (displayName, photoURL) => {
  if (!auth?.currentUser) throw new Error('No user logged in');
  try {
    await updateProfile(auth.currentUser, { displayName, photoURL });
    return true;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

// Send password reset email
export const sendPasswordReset = async email => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    console.error('Error sending password reset:', error);
    throw error;
  }
};

// Update user password
export const updateUserPassword = async newPassword => {
  if (!auth?.currentUser) throw new Error('No user logged in');
  try {
    await updatePassword(auth.currentUser, newPassword);
    return true;
  } catch (error) {
    console.error('Error updating password:', error);
    throw error;
  }
};

// Send email verification
export const sendVerificationEmail = async () => {
  if (!auth?.currentUser) throw new Error('No user logged in');
  try {
    await sendEmailVerification(auth.currentUser);
    return true;
  } catch (error) {
    console.error('Error sending verification:', error);
    throw error;
  }
};

// =============================================
// HELPER FUNCTIONS FOR STORAGE
// =============================================

// Upload file to storage
export const uploadFile = async (path, file) => {
  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return { url, path };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

// Upload file with progress tracking
export const uploadFileWithProgress = async (path, file, onProgress) => {
  try {
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        snapshot => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) onProgress(progress);
        },
        error => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({ url, path });
        },
      );
    });
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

// Delete file from storage
export const deleteFile = async path => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

// Get file URL
export const getFileUrl = async path => {
  try {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error('Error getting file URL:', error);
    return null;
  }
};

// List all files in a directory
export const listFiles = async path => {
  try {
    const storageRef = ref(storage, path);
    const result = await listAll(storageRef);
    return result.items;
  } catch (error) {
    console.error('Error listing files:', error);
    return [];
  }
};

// =============================================
// HELPER FUNCTIONS FOR ANALYTICS
// =============================================

// Log custom event
export const logAnalyticsEvent = (eventName, eventParams = {}) => {
  if (analytics && typeof window !== 'undefined') {
    logEvent(analytics, eventName, eventParams);
  }
};

// Set user ID for analytics
export const setAnalyticsUserId = userId => {
  if (analytics && userId) {
    setUserId(analytics, userId);
  }
};

// Set user properties for analytics
export const setAnalyticsUserProperties = properties => {
  if (analytics && properties) {
    setUserProperties(analytics, properties);
  }
};

// =============================================
// HELPER FUNCTIONS FOR PERFORMANCE
// =============================================

// Start a performance trace
export const startPerformanceTrace = traceName => {
  if (performance) {
    return trace(performance, traceName);
  }
  return null;
};

// =============================================
// HELPER FUNCTIONS FOR REMOTE CONFIG
// =============================================

// Fetch and activate remote config
export const fetchRemoteConfig = async () => {
  if (!remoteConfig) return null;
  try {
    await fetchAndActivate(remoteConfig);
    return getAll(remoteConfig);
  } catch (error) {
    console.error('Error fetching remote config:', error);
    return null;
  }
};

// Get remote config value
export const getRemoteConfigValue = key => {
  if (!remoteConfig) return null;
  try {
    return getValue(remoteConfig, key);
  } catch (error) {
    console.error('Error getting remote config value:', error);
    return null;
  }
};

// Get remote config string
export const getRemoteConfigString = key => {
  if (!remoteConfig) return null;
  try {
    return getString(remoteConfig, key);
  } catch (error) {
    console.error('Error getting remote config string:', error);
    return null;
  }
};

// Get remote config number
export const getRemoteConfigNumber = key => {
  if (!remoteConfig) return null;
  try {
    return getNumber(remoteConfig, key);
  } catch (error) {
    console.error('Error getting remote config number:', error);
    return null;
  }
};

// Get remote config boolean
export const getRemoteConfigBoolean = key => {
  if (!remoteConfig) return null;
  try {
    return getBoolean(remoteConfig, key);
  } catch (error) {
    console.error('Error getting remote config boolean:', error);
    return null;
  }
};

// =============================================
// FIRESTORE UTILITIES
// =============================================

// Get document count from collection
export const getCollectionCount = async collectionRef => {
  try {
    const snapshot = await getCountFromServer(collectionRef);
    return snapshot.data().count;
  } catch (error) {
    console.error('Error getting count:', error);
    return 0;
  }
};

// Run transaction
export const runFirestoreTransaction = async transactionCallback => {
  try {
    return await runTransaction(db, transactionCallback);
  } catch (error) {
    console.error('Transaction failed:', error);
    throw error;
  }
};

// Enable/disable network
export const setNetworkEnabled = async enabled => {
  if (enabled) {
    await enableNetwork(db);
  } else {
    await disableNetwork(db);
  }
};

// =============================================
// EXPORTS - All Firestore Functions
// =============================================

// Auth
export {
  auth,
  provider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  updateEmail,
  updateProfile,
  confirmPasswordReset,
  applyActionCode,
  verifyPasswordResetCode,
  checkActionCode,
};

// Firestore - Complete exports
export {
  db,
  collection,
  doc,
  setDoc,
  getDocs,
  getDoc,
  deleteDoc,
  updateDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  startAt,
  endAt,
  endBefore,
  limitToLast,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  Timestamp,
  writeBatch,
  increment,
  addDoc,
  onSnapshot,
  collectionGroup,
  documentId,
  getCountFromServer,
  runTransaction,
  enableNetwork,
  disableNetwork,
  connectFirestoreEmulator,
  enableIndexedDbPersistence,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  CACHE_SIZE_UNLIMITED,
};

// Storage
export {
  storage,
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
  updateMetadata,
  uploadString,
};

// Analytics
export {
  analytics,
  logEvent,
  setCurrentScreen,
  setUserId,
  setUserProperties,
  isSupported,
};

// Performance
export { performance, trace };

// Remote Config
export {
  remoteConfig,
  fetchAndActivate,
  getValue,
  getString,
  getNumber,
  getBoolean,
  getAll,
  setLogLevel,
};

// App instance
export default app;
